import { useEffect, useState, type FormEvent } from 'react';
import { ShieldCheck, KeyRound, Mail, Loader2 } from 'lucide-react';
import {
  Button,
  Input,
  Card,
  FormField,
  NotificationCard,
  UniversalLoadingState,
  Modal,
  ModalBody,
  ModalFooter,
  useToast,
} from '@afrilink/ui';
import { supabase } from '@/shared/lib/supabase';
import { validatePassword } from '@/shared/utils/passwordPolicy';

/**
 * SettingsSecurityPage (S3.2) — password + MFA + sign-in info.
 *
 * Password change uses supabase.auth.updateUser; MFA shows current
 * enrolment status and offers a TOTP enrol path. Multi-session listing
 * isn't surfaced by Supabase Auth's public API, so v1 shows the current
 * session timestamp + a "Sign out everywhere" action that calls
 * supabase.auth.signOut({ scope: 'global' }).
 */

interface UserInfo {
  email: string | null;
  emailVerified: boolean;
  lastSignInAt: string | null;
}

interface MfaState {
  enrolled: boolean;
  factorId?: string;
}

export default function SettingsSecurityPage() {
  const { toast } = useToast();
  const [user, setUser] = useState<UserInfo | null>(null);
  const [mfa, setMfa] = useState<MfaState>({ enrolled: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Password change
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [savingPw, setSavingPw] = useState(false);

  // Per-action busy flags
  const [resendingVerify, setResendingVerify] = useState(false);
  const [unenrolling, setUnenrolling] = useState(false);
  const [signingOutAll, setSigningOutAll] = useState(false);

  // TOTP enrol modal
  const [enrolOpen, setEnrolOpen] = useState(false);

  async function reloadMfaStatus() {
    const { data: factors } = await supabase.auth.mfa.listFactors();
    const totp = factors?.totp?.find((f) => f.status === 'verified');
    setMfa(totp ? { enrolled: true, factorId: totp.id } : { enrolled: false });
  }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const { data: { user: u } } = await supabase.auth.getUser();
        if (!u) throw new Error('Not signed in.');
        if (cancelled) return;
        setUser({
          email: u.email ?? null,
          emailVerified: !!u.email_confirmed_at,
          lastSignInAt: u.last_sign_in_at ?? null,
        });

        const { data: factors } = await supabase.auth.mfa.listFactors();
        if (cancelled) return;
        const totp = factors?.totp?.find((f) => f.status === 'verified');
        if (totp) setMfa({ enrolled: true, factorId: totp.id });
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => { cancelled = true; };
  }, []);

  async function onChangePassword(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // S-6: this screen used to accept 8 characters with no character classes,
    // while sign-up and password-reset both required 10 + mixed case + a digit.
    // One policy now, shared with those two and matched by the server floor.
    const pwProblem = validatePassword(pw1, pw2);
    if (pwProblem) {
      toast({ tone: 'warning', title: 'Choose a stronger password', body: pwProblem });
      return;
    }
    setSavingPw(true);
    try {
      const { error: upErr } = await supabase.auth.updateUser({ password: pw1 });
      if (upErr) throw new Error(upErr.message);
      setPw1('');
      setPw2('');
      toast({ tone: 'success', title: 'Password changed' });
    } catch (err) {
      toast({ tone: 'danger', title: 'Could not change password', body: err instanceof Error ? err.message : String(err) });
    } finally {
      setSavingPw(false);
    }
  }

  async function onResendVerification() {
    if (resendingVerify) return;
    if (!user?.email) return;
    setResendingVerify(true);
    try {
      const { error: rErr } = await supabase.auth.resend({ type: 'signup', email: user.email });
      if (rErr) {
        toast({ tone: 'danger', title: 'Could not send', body: rErr.message });
        return;
      }
      toast({ tone: 'success', title: 'Verification email sent', body: `Check ${user.email}.` });
    } finally {
      setResendingVerify(false);
    }
  }

  async function onUnenrolMfa() {
    if (unenrolling) return;
    if (!mfa.factorId) return;
    const ok = window.confirm('Disable two-factor authentication on your account?');
    if (!ok) return;
    setUnenrolling(true);
    try {
      const { error: ueErr } = await supabase.auth.mfa.unenroll({ factorId: mfa.factorId });
      if (ueErr) {
        toast({ tone: 'danger', title: 'Could not disable MFA', body: ueErr.message });
        return;
      }
      setMfa({ enrolled: false });
      toast({ tone: 'success', title: 'Two-factor authentication disabled' });
    } finally {
      setUnenrolling(false);
    }
  }

  async function onSignOutEverywhere() {
    if (signingOutAll) return;
    const ok = window.confirm('Sign out of every device, including this one?');
    if (!ok) return;
    setSigningOutAll(true);
    try {
      await supabase.auth.signOut({ scope: 'global' });
      window.location.href = '/sign-in';
    } finally {
      setSigningOutAll(false);
    }
  }

  if (loading) {
    return (
      <Card interactive={false} className="p-12">
        <UniversalLoadingState variant="spinner" caption="Loading security settings…" />
      </Card>
    );
  }

  if (error || !user) {
    return <NotificationCard tone="danger" title="Could not load" body={error ?? 'Unknown error'} />;
  }

  return (
    <div className="space-y-6">
      {/* Email status */}
      <Card interactive={false}>
        <header className="border-b border-border px-6 py-3">
          <h3 className="text-sm font-semibold text-navy-primary inline-flex items-center gap-2">
            <Mail className="size-4" aria-hidden /> Email
          </h3>
        </header>
        <div className="flex items-center justify-between px-6 py-5 text-sm">
          <div>
            <div className="font-medium text-navy-primary">{user.email}</div>
            <div className="text-xs text-muted-foreground">
              {user.emailVerified ? 'Verified' : 'Not yet verified — some actions are blocked.'}
            </div>
          </div>
          {!user.emailVerified && (
            <Button size="sm" variant="outline" onClick={onResendVerification} loading={resendingVerify} disabled={resendingVerify}>
              Resend verification
            </Button>
          )}
        </div>
      </Card>

      {/* Password */}
      <Card interactive={false}>
        <header className="border-b border-border px-6 py-3">
          <h3 className="text-sm font-semibold text-navy-primary inline-flex items-center gap-2">
            <KeyRound className="size-4" aria-hidden /> Password
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Use at least 8 characters. Mixing letters, numbers, and a symbol helps.
          </p>
        </header>
        <form onSubmit={onChangePassword} className="space-y-4 px-6 py-5">
          <FormField label="New password" htmlFor="pw1">
            <Input
              id="pw1"
              type="password"
              value={pw1}
              onChange={(e) => setPw1(e.target.value)}
              autoComplete="new-password"
              disabled={savingPw}
              required
              minLength={8}
            />
          </FormField>
          <FormField label="Confirm new password" htmlFor="pw2">
            <Input
              id="pw2"
              type="password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              autoComplete="new-password"
              disabled={savingPw}
              required
            />
          </FormField>
          <div className="flex justify-end">
            <Button type="submit" disabled={savingPw} loading={savingPw}>
              {savingPw ? 'Saving…' : 'Change password'}
            </Button>
          </div>
        </form>
      </Card>

      {/* MFA */}
      <Card interactive={false}>
        <header className="border-b border-border px-6 py-3">
          <h3 className="text-sm font-semibold text-navy-primary inline-flex items-center gap-2">
            <ShieldCheck className="size-4" aria-hidden /> Two-factor authentication
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            A second factor stops anyone from signing in even if they know your password.
          </p>
        </header>
        <div className="flex items-center justify-between px-6 py-5 text-sm">
          <div>
            <div className="font-medium text-navy-primary">
              {mfa.enrolled ? 'Enabled' : 'Not enabled'}
            </div>
            <div className="text-xs text-muted-foreground">
              {mfa.enrolled
                ? 'You will be prompted for a TOTP code on every sign-in.'
                : 'Enrol a TOTP authenticator app to add a second factor.'}
            </div>
          </div>
          {mfa.enrolled ? (
            <Button size="sm" variant="outline" onClick={onUnenrolMfa} loading={unenrolling} disabled={unenrolling}>
              Disable
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setEnrolOpen(true)}>
              Enrol TOTP
            </Button>
          )}
        </div>
      </Card>

      <TotpEnrolModal
        open={enrolOpen}
        onClose={() => setEnrolOpen(false)}
        onEnrolled={async () => {
          setEnrolOpen(false);
          await reloadMfaStatus();
          toast({ tone: 'success', title: 'Two-factor authentication enabled' });
        }}
      />

      {/* Sessions */}
      <Card interactive={false}>
        <header className="border-b border-border px-6 py-3">
          <h3 className="text-sm font-semibold text-navy-primary">Sign-in sessions</h3>
        </header>
        <div className="flex items-center justify-between px-6 py-5 text-sm">
          <div>
            <div className="font-medium text-navy-primary">Current session</div>
            <div className="text-xs text-muted-foreground">
              {user.lastSignInAt ? `Signed in ${new Date(user.lastSignInAt).toLocaleString('en-ZA')}` : 'Active'}
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={onSignOutEverywhere} loading={signingOutAll} disabled={signingOutAll}>
            Sign out everywhere
          </Button>
        </div>
      </Card>
    </div>
  );
}

/**
 * Full TOTP enrol flow:
 *   1. enroll() returns { id (factorId), totp.qr_code (data: URI), totp.secret }
 *   2. User scans QR with authenticator app → enters 6-digit code
 *   3. challengeAndVerify({ factorId, code }) — on success the factor flips
 *      from 'unverified' to 'verified' and counts toward AAL2.
 *
 * If the user closes the modal mid-flow, we unenrol the unverified factor so
 * repeat attempts don't pile up dead enrolments.
 */
function TotpEnrolModal({
  open,
  onClose,
  onEnrolled,
}: {
  open: boolean;
  onClose: () => void;
  onEnrolled: () => Promise<void> | void;
}) {
  const { toast } = useToast();
  const [step, setStep] = useState<'enrolling' | 'verify' | 'verifying'>('enrolling');
  const [factorId, setFactorId] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Begin enrolment when the modal opens
  useEffect(() => {
    if (!open) return;
    setStep('enrolling');
    setFactorId(null);
    setQr(null);
    setSecret(null);
    setCode('');
    setErrorMsg(null);
    void (async () => {
      try {
        // Pre-clean any unverified factors from a prior abandoned enrol
        const { data: existing } = await supabase.auth.mfa.listFactors();
        // Cast widens the type — Supabase's Factor.status is typed as the
        // literal 'verified' but the runtime value is 'verified' | 'unverified'.
        const stale = existing?.totp?.filter((f) => (f.status as string) === 'unverified') ?? [];
        for (const f of stale) {
          await supabase.auth.mfa.unenroll({ factorId: f.id });
        }

        const { data, error } = await supabase.auth.mfa.enroll({
          factorType: 'totp',
          friendlyName: `Authenticator (${new Date().toLocaleDateString('en-ZA')})`,
        });
        if (error) throw new Error(error.message);
        if (!data) throw new Error('Enrolment returned no data.');
        setFactorId(data.id);
        setQr(data.totp.qr_code);
        setSecret(data.totp.secret);
        setStep('verify');
      } catch (err) {
        setErrorMsg(err instanceof Error ? err.message : 'Enrolment failed.');
      }
    })();
  }, [open]);

  async function handleClose() {
    // If the user closes mid-flow, drop the unverified factor.
    if (factorId && step !== 'verifying') {
      try { await supabase.auth.mfa.unenroll({ factorId }); } catch { /* best-effort */ }
    }
    onClose();
  }

  async function onVerify(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!factorId) return;
    const cleaned = code.replace(/\s/g, '');
    if (!/^\d{6}$/.test(cleaned)) {
      toast({ tone: 'warning', title: 'Enter the 6-digit code from your app' });
      return;
    }
    setStep('verifying');
    setErrorMsg(null);
    try {
      const { error: verifyErr } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code: cleaned,
      });
      if (verifyErr) throw new Error(verifyErr.message);
      await onEnrolled();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Verification failed.');
      setStep('verify');
    }
  }

  return (
    <Modal
      open={open}
      onOpenChange={(v) => !v && void handleClose()}
      title="Enable two-factor authentication"
      description="Scan the QR with an authenticator app (Google Authenticator, 1Password, Authy, Bitwarden), then enter the 6-digit code below."
      size="wide"
    >
      <ModalBody>
        {step === 'enrolling' && (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> <span className="ml-2">Generating QR…</span>
          </div>
        )}
        {(step === 'verify' || step === 'verifying') && qr && (
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-3">
              {/* Supabase returns a data: URI for the QR */}
              <img
                src={qr}
                alt="TOTP QR code"
                width={200}
                height={200}
                className="rounded border border-border"
              />
              {secret && (
                <details className="text-xs text-muted-foreground">
                  <summary className="cursor-pointer">Can&apos;t scan? Enter the secret manually</summary>
                  <code className="mt-2 block break-all rounded bg-muted px-2 py-1 font-mono text-xs tabular-nums">
                    {secret}
                  </code>
                </details>
              )}
            </div>
            <form onSubmit={onVerify} className="space-y-3" id="totp-verify-form">
              <FormField label="6-digit code" htmlFor="totp-code" helper="The current code from your authenticator app">
                <Input
                  id="totp-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  maxLength={7}
                  autoFocus
                  disabled={step === 'verifying'}
                  className="text-center text-lg tracking-widest tabular-nums"
                />
              </FormField>
              {errorMsg && (
                <NotificationCard tone="danger" title="Could not verify" body={errorMsg} />
              )}
            </form>
          </div>
        )}
        {step === 'enrolling' && errorMsg && (
          <NotificationCard tone="danger" title="Could not start enrolment" body={errorMsg} />
        )}
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={() => void handleClose()} disabled={step === 'verifying'}>
          Cancel
        </Button>
        <Button
          form="totp-verify-form"
          type="submit"
          loading={step === 'verifying'}
          disabled={step !== 'verify'}
        >
          {step === 'verifying' ? 'Verifying…' : 'Enable two-factor'}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
